import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import React from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { LayerItem } from "./types";

interface Props {
  layer: LayerItem | null;
  onConfirm: (id: string, weight1: number, weight2: number) => void;
  onClose: () => void;
  massScaleFactor: number;
}

export const SplitLayerDialog = ({
  layer,
  onConfirm,
  onClose,
  massScaleFactor,
}: Props) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const [weight1, setWeight1] = React.useState("");
  const [weight2, setWeight2] = React.useState("");
  const [prevLayer, setPrevLayer] = React.useState(layer);
  const [prevScale, setPrevScale] = React.useState(massScaleFactor);

  if (layer !== prevLayer || massScaleFactor !== prevScale) {
    setPrevLayer(layer);
    setPrevScale(massScaleFactor);
    if (layer) {
      const displayTotal = layer.weight * massScaleFactor;
      const half = displayTotal / 2;
      setWeight1(String(Math.round(half * 100) / 100));
      setWeight2(String(Math.round((displayTotal - half) * 100) / 100));
    }
  }

  const handleConfirm = () => {
    if (!layer) return;
    const parsedFirst = parseFloat(weight1);
    const parsedSecond = parseFloat(weight2);
    if (
      !isNaN(parsedFirst) &&
      !isNaN(parsedSecond) &&
      parsedFirst > 0 &&
      parsedSecond > 0
    ) {
      onConfirm(
        layer.id,
        parsedFirst / massScaleFactor,
        parsedSecond / massScaleFactor
      );
      onClose();
    }
  };

  return (
    <Dialog open={layer !== null} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("splitLayer")}</DialogTitle>
      <DialogContent>
        {layer && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {layer.materialName} —{" "}
              {formatNumber(2, true)(layer.weight * massScaleFactor)}
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                type="number"
                size="small"
                value={weight1}
                onChange={(e) => {
                  setWeight1(e.target.value);
                }}
                slotProps={{ htmlInput: { step: "any", min: 0 } }}
                fullWidth
              />
              <TextField
                type="number"
                size="small"
                value={weight2}
                onChange={(e) => {
                  setWeight2(e.target.value);
                }}
                slotProps={{ htmlInput: { step: "any", min: 0 } }}
                fullWidth
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button variant="contained" onClick={handleConfirm}>
          {t("splitLayer")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
